import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { SEO, seoConfig } from "@/hooks/useSEO";
import { generateWebPageSchema, generateBreadcrumbSchema, generateArticleSchema } from "@/lib/jsonld";

// Images
import coverImage from "@/assets/blog/kpi-fidelite-client/couverture.webp";
import satisfactionImage from "@/assets/blog/kpi-fidelite-client/satisfaction-vs-fidelite.webp";
import reachatImage from "@/assets/blog/kpi-fidelite-client/taux-reachat.webp";
import clvImage from "@/assets/blog/kpi-fidelite-client/calcul-clv.webp";
import dashboardImage from "@/assets/blog/kpi-fidelite-client/dashboard-kpi.webp";

const KpiFideliteClient = () => {
  const jsonLdSchemas = [
    generateWebPageSchema(
      "Comment mesurer la fidélité client : les 6 KPI indispensables",
      seoConfig.blogKpiFideliteClient.description,
      "/mesurer-fidelite-client-kpi"
    ),
    generateBreadcrumbSchema([
      { name: "Accueil", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: "Mesurer la fidélité client", path: "/mesurer-fidelite-client-kpi" }
    ]),
    generateArticleSchema(
      "Comment mesurer la fidélité client : les 6 KPI indispensables (et comment les calculer)",
      seoConfig.blogKpiFideliteClient.description,
      "https://unotif.com/mesurer-fidelite-client-kpi",
      "2026-03-25"
    )
  ];

  const imageStyle = {
    boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO {...seoConfig.blogKpiFideliteClient} jsonLd={jsonLdSchemas} />
      <Header />

      <main className="flex-1">
        {/* Hero Section */}
        <section className="pt-24 lg:pt-40 pb-12 sm:pb-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Breadcrumb */}
            <nav className="text-sm font-medium text-[#495456] mb-6">
              <Link to="/blog" className="text-[#0c97a9] hover:underline">Blog</Link>
              <span className="mx-2">›</span>
              <span className="text-[#074155]">Mesurer la fidélité client</span>
            </nav>

            {/* H1 */}
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-8">
              Comment mesurer la fidélité client : les 6 KPI indispensables (et comment les calculer)
            </h1>

            {/* Cover Image */}
            <img
              src={coverImage}
              alt="Illustration des KPI de fidélité client avec indicateurs et analyse de performance pour mesurer la rétention"
              loading="eager"
              className="rounded-2xl w-full h-auto"
              style={imageStyle}
            />
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 sm:pb-28 lg:pb-36">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">

            {/* Introduction */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Investir dans un programme de fidélisation sans indicateurs de suivi, c'est avancer sans repères. La plupart des commerçants et entrepreneurs savent instinctivement si leurs clients "reviennent", mais très peu mesurent précisément ce qui se passe. Ils continuent d'investir du temps et de l'argent dans des outils sans savoir lesquels produisent des résultats réels.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Cet article présente les <strong className="font-semibold">6 KPI indispensables pour mesurer la fidélité client</strong>, avec pour chacun la formule exacte, un exemple de calcul et les seuils de référence. Si vous êtes encore en train de choisir vos outils de fidélisation, notre guide sur les <Link to="/meilleurs-outils-de-fidelisation-client" className="text-[#0c97a9] hover:underline font-semibold">outils de fidélisation client</Link> vous donnera une vue d'ensemble complète avant d'entrer dans les chiffres.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* H2 - Pourquoi mesurer la fidélité client */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Pourquoi mesurer la fidélité client, et pas seulement la satisfaction ?
            </h2>

            {/* Image satisfaction vs fidélité */}
            <img
              src={satisfactionImage}
              alt="Infographie comparant satisfaction client et fidélité client avec différence entre moment ponctuel et relation dans le temps"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un client satisfait et un client fidèle ne sont pas la même chose. La satisfaction est une photo instantanée : elle mesure le ressenti à un moment précis. La fidélité est un film sur la durée : elle mesure les comportements répétés, les retours réguliers, l'attachement construit visite après visite.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La confusion entre les deux coûte cher. Un taux de satisfaction à 90 % ne garantit pas un taux de rétention élevé. Des clients peuvent partir malgré une bonne expérience, simplement parce qu'un concurrent leur a proposé une raison plus forte de revenir chez lui.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Un client fidèle vaut 5 à 7 fois plus qu'un nouveau client</strong> (Bain & Company). Sans indicateurs précis, il est impossible de savoir quelle action a produit quel résultat, et donc impossible de reproduire ce qui fonctionne.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* KPI n°1 : taux de rétention */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              KPI n°1 : le taux de rétention client
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">taux de rétention</strong> mesure le pourcentage de clients qui restent actifs d'une période à l'autre. C'est le premier indicateur à mettre en place : il donne la vue la plus directe sur l'effet réel de votre programme de fidélisation.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Formule :</strong> ((Clients en fin de période - Nouveaux clients sur la période) / Clients en début de période) × 100
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Exemple concret :</strong> vous aviez 100 clients en janvier. Vous en avez gagné 20 dans le mois. Vous en avez 105 en fin de mois.
              <br />Calcul : ((105 - 20) / 100) × 100 = <strong className="font-semibold">85 %</strong>
            </p>

            {/* Tableau rétention */}
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">Taux de rétention</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Interprétation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Supérieur à 80 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Excellent pour un commerce local ou un indépendant</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Entre 60 et 80 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Correct : des progrès sont possibles sans refonte complète</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-[#495456]">Inférieur à 60 %</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Signal d'alerte : revoir l'expérience client et le programme en place</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le taux de rétention se mesure sur la période la plus courte qui ait du sens pour votre activité : mensuel pour un café ou une boutique, trimestriel pour un coach ou un prestataire de services.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* KPI n°2 : taux de réachat */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              KPI n°2 : le taux de réachat
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">taux de réachat</strong> indique la proportion de clients ayant effectué au moins deux achats sur une période donnée. C'est l'indicateur le plus direct pour évaluer si votre programme de fidélité génère des retours concrets.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Formule :</strong> (Nombre de clients ayant racheté / Total de clients actifs sur la période) × 100
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Référence :</strong> un taux de réachat supérieur à 30 % est un bon seuil pour un commerce physique. En dessous, le programme de fidélisation n'a pas encore d'effet mesurable sur les comportements d'achat.
            </p>

            {/* Image taux de réachat */}
            <img
              src={reachatImage}
              alt="Schéma du taux de réachat client avec distinction entre clients uniques et clients fidèles avec calcul illustré"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ce KPI est particulièrement utile après le lancement d'un nouveau programme ou d'une campagne de réactivation. Il répond à une question précise : "Mes clients reviennent-ils plus souvent depuis que j'ai mis ça en place ?" Si la réponse est non après 60 jours, la mécanique du programme mérite d'être revue.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* KPI n°3 : CLV */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              KPI n°3 : la Customer Lifetime Value (CLV)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">Customer Lifetime Value</strong>, ou valeur vie client, représente le revenu total qu'un client va générer pendant toute la durée de sa relation avec votre commerce. C'est l'indicateur stratégique par excellence : s'il progresse d'une année sur l'autre, vos efforts de fidélisation portent leurs fruits.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Formule simplifiée :</strong> Panier moyen × Fréquence d'achat annuelle × Durée de la relation (en années)
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Exemple :</strong> panier moyen de 40 €, 6 visites par an, relation sur 3 ans.
              <br />Calcul : 40 × 6 × 3 = <strong className="font-semibold">CLV de 720 €</strong>
            </p>

            {/* Image CLV */}
            <img
              src={clvImage}
              alt="Schéma du calcul CLV avec exemple panier moyen, fréquence annuelle et durée de relation pour mesurer la fidélisation client"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La CLV remplit deux fonctions distinctes. Elle dit combien vaut réellement un client dans votre activité. Et elle permet de calculer combien vous pouvez investir pour en acquérir un nouveau ou en fidéliser un existant tout en restant rentable. Un programme de fidélisation qui coûte 50 € par mois et qui augmente la CLV de 200 € sur un an est clairement rentable. Sans ce calcul, cette décision reste une intuition.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* KPI n°4 : NPS */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              KPI n°4 : le Net Promoter Score (NPS)
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">Net Promoter Score</strong> mesure la probabilité qu'un client recommande votre commerce à son entourage. Il repose sur une seule question posée à vos clients : "Sur une échelle de 0 à 10, quelle est la probabilité que vous nous recommandiez à un ami ou un proche ?"
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Les réponses se répartissent en trois catégories :
            </p>

            {/* Tableau NPS */}
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">Score</th>
                    <th className="px-4 py-3 text-left font-semibold">Catégorie</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Ce que ça signifie</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">9 ou 10</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Promoteurs</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Clients engagés, ambassadeurs actifs de votre commerce</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">7 ou 8</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Passifs</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Satisfaits mais pas encore engagés, susceptibles de partir</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-[#495456]">0 à 6</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Détracteurs</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Déçus, risque de commentaires négatifs dans leur entourage</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Formule NPS :</strong> % de Promoteurs - % de Détracteurs
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un NPS supérieur à 50 est considéré comme excellent. La mesure se fait une fois par trimestre, via un formulaire simple envoyé à votre base. Le NPS n'est pas qu'un score de satisfaction : c'est un indicateur d'engagement émotionnel. Un client qui recommande activement votre commerce affiche un <strong className="font-semibold">taux de rétention</strong> et une <strong className="font-semibold">CLV</strong> systématiquement plus élevés que la moyenne.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* KPI n°5 : taux de rédemption */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              KPI n°5 : le taux de rédemption des récompenses
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le <strong className="font-semibold">taux de rédemption</strong> mesure l'attractivité réelle de votre programme de fidélité : quelle proportion des récompenses distribuées est effectivement utilisée par vos clients.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Formule :</strong> (Nombre de récompenses utilisées / Nombre de récompenses distribuées) × 100
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Seuil de référence :</strong> en dessous de 20 %, vos récompenses ne sont pas assez attractives, ou vos clients ne savent pas qu'ils en ont une disponible. C'est l'indicateur le plus direct pour diagnostiquer une erreur de calibration dans votre programme.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ce KPI se lit en parallèle du <strong className="font-semibold">taux de rétention</strong>. Un programme qui retient ses clients mais dont personne n'utilise les récompenses repose sur autre chose qu'un mécanisme de points : l'expérience, la proximité, la qualité du service. L'inverse, un taux de rédemption élevé couplé à une faible rétention, indique que les clients reviennent chercher leur récompense puis disparaissent.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* KPI n°6 : fréquence de visite */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              KPI n°6 : la fréquence de visite
            </h2>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              La <strong className="font-semibold">fréquence de visite</strong> mesure le nombre moyen de passages par client actif sur une période donnée. C'est l'indicateur qui répond à la question que tout commerçant se pose : "Mon programme de fidélité incite-t-il vraiment mes clients à revenir plus souvent ?"
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Calcul :</strong> Nombre total de visites sur 90 jours / Nombre de clients actifs sur la même période
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              <strong className="font-semibold">Référence :</strong> les données issues des commerces utilisant une carte wallet montrent que les clients actifs sur ce canal visitent en moyenne 37 % plus souvent que les clients sans carte active. Le wallet mobile vous donne accès à ces métriques en temps réel, sans configuration complexe. Pour comprendre pourquoi ce canal génère un engagement aussi élevé, notre guide sur les <Link to="/notifications-push-wallet-fidelisation" className="text-[#0c97a9] hover:underline font-semibold">notifications push wallet : le canal de fidélisation qui cartonne en 2026</Link> détaille les chiffres et les mécanismes.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Comment suivre ces 6 KPI */}
            <h2 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4 mt-12">
              Comment suivre ces 6 KPI sans y passer des heures
            </h2>

            {/* Image dashboard */}
            <img
              src={dashboardImage}
              alt="Tableau de bord des KPI de fidélisation client avec métriques, tendances et fréquence de suivi"
              loading="lazy"
              className="rounded-2xl w-full h-auto my-8"
              style={imageStyle}
            />

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Le tableau de bord minimal pour piloter votre fidélisation :
            </p>

            {/* Tableau suivi KPI */}
            <div className="overflow-x-auto my-8">
              <table className="w-full border-collapse text-[13px] sm:text-sm">
                <thead>
                  <tr className="bg-[#074155] text-white">
                    <th className="px-4 py-3 text-left font-semibold rounded-tl-xl">KPI</th>
                    <th className="px-4 py-3 text-left font-semibold">Fréquence de suivi</th>
                    <th className="px-4 py-3 text-left font-semibold rounded-tr-xl">Outil minimal requis</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux de rétention</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Mensuel</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Tableur ou CRM</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux de réachat</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Mensuel</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Logiciel de caisse ou CRM</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Customer Lifetime Value</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Trimestriel</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Tableur</td>
                  </tr>
                  <tr className="border-b border-gray-100 bg-gray-50/50">
                    <td className="px-4 py-3 font-medium text-[#495456]">NPS</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Trimestriel</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Google Forms ou outil dédié</td>
                  </tr>
                  <tr className="border-b border-gray-100">
                    <td className="px-4 py-3 font-medium text-[#495456]">Taux de rédemption</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Mensuel</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Plateforme de fidélité (ex. Unotif)</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium text-[#495456]">Fréquence de visite</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Mensuel</td>
                    <td className="px-4 py-3 font-medium text-[#495456]">Plateforme de fidélité</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ne cherchez pas à suivre les 6 indicateurs dès le premier jour. Commencez par trois : le <strong className="font-semibold">taux de rétention</strong>, le <strong className="font-semibold">taux de réachat</strong> et la <strong className="font-semibold">fréquence de visite</strong>. Ces trois données donnent une vision suffisante pour prendre des décisions dans les 90 premiers jours. Ajoutez la CLV et le NPS au trimestre suivant, quand les premières séries de données sont disponibles.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Un tableur Google Sheets suffit pour démarrer. Deux colonnes par KPI, une ligne par période, un graphique simple pour visualiser les tendances : c'est assez pour piloter sérieusement un programme de fidélisation sans y passer des heures chaque semaine.
            </p>

            <hr className="my-10 border-gray-200" />

            {/* Conclusion */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Ces <strong className="font-semibold">6 indicateurs de fidélisation</strong> permettent de passer d'une gestion intuitive à une gestion basée sur des données réelles. Commencez par en choisir deux ou trois, mesurez-les régulièrement, et prenez des décisions à partir de ce qu'ils révèlent. Au fil des mois, les tendances se dessinent et les ajustements deviennent précis.
            </p>
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] mb-4">
              Unotif centralise automatiquement les données de rédemption et de fréquence de visite dans un tableau de bord accessible dès la configuration. Testez gratuitement et commencez à piloter votre fidélisation avec des chiffres réels dès cette semaine.
            </p>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default KpiFideliteClient;
